import React, { useEffect } from 'react';
import { Container, Typography } from '@mui/material';
import { ListContent } from '@presentation/layouts/styles';
import { ListHeader } from '@presentation/components/ListHeader';
import type { CreatorsService } from '@domain/usecases/creators/CreatorsService';
import { useCreatorContents } from '@presentation/pages/Creator/hooks/useCreatorContents';
import type { PuzzleSortField } from '@domain/usecases/puzzles/PuzzleSortField';
import { ArtworkInfiniteScrollList } from '@presentation/components/ArtworkInfiniteScrollList';
import { useSmartBanner } from '@presentation/context/SmartBannerContextProvider';
import type { CreatorModel } from '@domain/models';
import { JsonLd } from '@presentation/seo/JsonLd';
import { buildBreadcrumb, buildCreatorGraph } from '@presentation/seo/schema';

import { AuthorHeader } from './components/AuthorHeader';
import { SORT_OPTIONS } from './constants';

const CreatorPage = ({
	creatorsService,
	firstPage,
	creator,
	intro,
}: {
	creatorsService: CreatorsService;
	firstPage: any;
	creator: CreatorModel;
	intro?: string;
}) => {
	const { isOpen, generateLink } = useSmartBanner();
	const { data, fetchContent, hasMore, handleSort, sort } = useCreatorContents(creator?.id, firstPage, creatorsService);

	useEffect(() => {
		if (creator.id) {
			generateLink('ARTIST', creator.id);
		}
	}, [creator.id, generateLink]);

	return (
		<>
			<JsonLd data={buildCreatorGraph(creator, `/${creator.profileLink}`)} />
			<JsonLd
				data={buildBreadcrumb([
					{ name: 'Home', url: '/' },
					{ name: 'Creators', url: '/creators' },
					{ name: creator.name },
				])}
			/>
			<AuthorHeader authorData={creator} />
			{intro && (
				<Container maxWidth='xl'>
					<Typography
						component='h2'
						sx={{
							px: 2,
							mt: 2,
							fontSize: { xs: 15, md: 17 },
							fontWeight: 400,
							lineHeight: 1.7,
							color: 'text.primary',
							maxWidth: 900,
							textWrap: 'pretty',
						}}
					>
						{intro}
					</Typography>
				</Container>
			)}
			<ListContent isSmartBannerOpen={isOpen} maxWidth='xl'>
				<ListHeader
					sortOptions={SORT_OPTIONS}
					sortBy={`${sort.field}|${sort.direction}`}
					onSortChange={(orderBy, orderDirection) => handleSort(orderBy as PuzzleSortField, orderDirection)}
				/>
				<ArtworkInfiniteScrollList
					data={data.map((item) => ({
						...item,
						author: creator,
					}))}
					hasMore={hasMore}
					loadMore={() => fetchContent()}
				/>
			</ListContent>
		</>
	);
};

export default CreatorPage;
